import type { Icon as TablerIcon } from "@tabler/icons-react";
import * as TablerIcons from "@tabler/icons-react";

export type TablerIconName = string;

// Convert kebab-case to PascalCase for Icon lookup
// e.g., "brand-github" → "IconBrandGithub"
function kebabToPascalIcon(kebab: string): string {
  return (
    "Icon" +
    kebab
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("")
  );
}

// Use the package's iconsList for all icon names
const iconNames: string[] = (TablerIcons.iconsList as { default: string[] }).default;

// Build icon list with components
function buildIconList(): Array<{ name: string; component: TablerIcon }> {
  const icons: Array<{ name: string; component: TablerIcon }> = [];

  for (const name of iconNames) {
    const componentName = kebabToPascalIcon(name);
    const component = (TablerIcons as Record<string, unknown>)[componentName];

    // React components have $$typeof property
    if (component && typeof component === "object" && "$$typeof" in component) {
      icons.push({ name, component: component as TablerIcon });
    }
  }

  return icons;
}

export const TABLER_ICONS = buildIconList();

const TABLER_ICON_MAP = new Map(TABLER_ICONS.map((i) => [i.name, i.component]));

export function getTablerIcon(name: string): TablerIcon | undefined {
  return TABLER_ICON_MAP.get(name);
}

export function searchTablerIcons(query: string, limit = 30) {
  if (!query) return TABLER_ICONS.slice(0, limit);
  const q = query.toLowerCase();
  return TABLER_ICONS.filter((i) => i.name.includes(q)).slice(0, limit);
}

export function getIconsPage(
  query: string,
  page: number,
  pageSize = 60,
): { icons: Array<{ name: string; component: TablerIcon }>; hasMore: boolean } {
  const filtered = query
    ? TABLER_ICONS.filter((i) => i.name.includes(query.toLowerCase()))
    : TABLER_ICONS;

  const start = page * pageSize;
  const icons = filtered.slice(start, start + pageSize);
  const hasMore = start + pageSize < filtered.length;

  return { icons, hasMore };
}
